import { useState } from "react";
import { RotateCcw } from "lucide-react";
import { formatPriceCents, parsePriceInput } from "@/components/services/service-meta";
import { cn } from "@/lib/cn";

export type PriceBadge = "custom" | "adjusted" | "global" | null;

export type PriceValue = {
  priceCents: number | null;
  badge: PriceBadge;
  hasOverride: boolean;
};

const BADGE_LABEL: Record<Exclude<PriceBadge, null>, string> = {
  custom: "Personalizado",
  adjusted: "Global (ajustado)",
  global: "Global",
};

type Props = PriceValue & {
  onSave: (priceCents: number) => void;
  onRestore?: () => void;
  saving?: boolean;
};

/** Input de preço com selo de origem (Global/ajustado/personalizado) + botão de restaurar padrão
 *  quando há override — reaproveitado tanto na linha "flat" de um serviço quanto nas linhas de
 *  material dentro dele (ver PriceTable). */
export function PriceInputCell({ priceCents, badge, hasOverride, onSave, onRestore, saving = false }: Props) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");

  const displayValue = editing ? value : priceCents != null ? formatPriceCents(priceCents) : "";

  const handleFocus = () => {
    setEditing(true);
    setValue(priceCents != null ? (priceCents / 100).toFixed(2).replace(".", ",") : "");
  };

  const handleBlur = () => {
    setEditing(false);
    const cents = parsePriceInput(value);
    if (value.trim() !== "" && cents !== priceCents) onSave(cents);
  };

  return (
    <div className="flex shrink-0 items-center justify-end gap-1.5">
      {badge && (
        <span
          className={cn(
            "shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider",
            badge === "custom"
              ? "bg-brand-100 text-brand-700 dark:bg-primary/15 dark:text-primary"
              : "bg-zinc-100 text-zinc-500 dark:bg-muted dark:text-muted-foreground",
          )}
        >
          {BADGE_LABEL[badge]}
        </span>
      )}
      <input
        value={displayValue}
        onFocus={handleFocus}
        onChange={(e) => setValue(e.target.value)}
        onBlur={handleBlur}
        placeholder="—"
        disabled={saving}
        className="h-8 w-24 rounded-md border border-zinc-200 bg-white px-2 text-right text-[13px] text-zinc-900 dark:border-border dark:bg-card dark:text-foreground"
      />
      {hasOverride && onRestore && (
        <button
          type="button"
          onClick={onRestore}
          title="Restaurar padrão"
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-600 dark:hover:bg-muted"
        >
          <RotateCcw className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
